import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from './PartnerDevice.module.css';
import RegisterDeviceModal from './RegisterDeviceModal';
import UpgradeMembershipModal from '../Fleet/UpgradeMembershipModal';
import { SwapMainAppContext } from '../../../context/SwapMainAppContext';
import useBackgroundColorChange from '../../../utils/useBackgroundColorChange';

const VALUE_PROPS = [
    {
        title: 'A ready market',
        sub: 'Reach operators actively shopping for capture hardware — no audience to build from scratch.',
        icon: (
            <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
                <path d="M8.5 1.5l2 4.3 4.7.5-3.5 3.2 1 4.6-4.2-2.4L4 14.1l1-4.6L1.5 6.3l4.7-.5z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
            </svg>
        ),
    },
    {
        title: 'Sell into the fleet',
        sub: 'Your devices appear on our fleet page where operators buy them directly to start capturing.',
        icon: (
            <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
                <path d="M2 14V9M6.5 14V5M11 14V7M15.5 14V3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
            </svg>
        ),
    },
    {
        title: 'Verified onboarding',
        sub: 'Register once with your serial scheme — we validate every unit against it so only genuine devices get on the platform.',
        icon: (
            <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
                <circle cx="8.5" cy="8.5" r="6.5" stroke="currentColor" strokeWidth="1.2" />
                <path d="M5.5 8.5l2 2 4-4.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ),
    },
];

const HOW_IT_WORKS_STEPS = [
    {
        title: 'Register your device',
        sub: 'Share the model details and your serial number format in a two-minute form.',
    },
    {
        title: 'We verify it',
        sub: 'We validate your serial scheme, then set up a follow-up call to go over and confirm your listing details.',
    },
    {
        title: 'Start selling',
        sub: 'Your device goes live on the fleet page for operators to buy and put to work.',
    },
];

function PartnerDevice() {
    const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [isPaymentRedirect, setIsPaymentRedirect] = useState(false);
    const { gatewayToken, userClass, showNotification } = useContext(SwapMainAppContext);
    const location = useLocation();
    const navigate = useNavigate();
    useBackgroundColorChange('#181614');

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        if (searchParams.get('payment') === 'success' && searchParams.has('session_id')) {
            setShowUpgradeModal(true);
            setIsPaymentRedirect(true);
            navigate(location.pathname, { replace: true });
        }
    }, [location.search, navigate]);

    const handleListDeviceClick = () => {
        if (!gatewayToken) {
            showNotification('warning', 'Please log in first');
            return;
        }
        if (userClass === 'Explorer Member') {
            setShowUpgradeModal(true);
            return;
        }
        setIsRegisterModalOpen(true);
    };

    const handleUpgradeClose = () => {
        setShowUpgradeModal(false);
        setIsPaymentRedirect(false);
    };

    const handleUpgradeSuccess = () => {
        setShowUpgradeModal(false);
        setIsPaymentRedirect(false);
        setIsRegisterModalOpen(true);
    };

    return (
        <div className={styles.wrap}>
            <div className={styles.hero}>
                <h1 className={styles.heroTitle}>
                    Sell your hardware to <em>thousands</em> of data operators.
                </h1>
                <p className={styles.heroLede}>
                    List your robot arms, manipulators, and ego data-capture devices in the PrismaX fleet.
                    Operators discover your hardware and buy it to capture demonstrations — putting your
                    devices in front of a market that&apos;s already collecting training data.
                </p>
                <div className={styles.heroCta}>
                    <button
                        type="button"
                        className={styles.btnPrimaryLg}
                        onClick={handleListDeviceClick}
                    >
                        List your device now
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M3 7h8M7.5 3.5l3.5 3.5-3.5 3.5" stroke="#111" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                </div>
            </div>

            <div className={styles.props}>
                {VALUE_PROPS.map((prop) => (
                    <div className={styles.prop} key={prop.title}>
                        <div className={styles.propIcon}>{prop.icon}</div>
                        <div className={styles.propTitle}>{prop.title}</div>
                        <div className={styles.propSub}>{prop.sub}</div>
                    </div>
                ))}
            </div>

            <div className={styles.how}>
                <div className={styles.howHead}>How it works</div>
                <div className={styles.stepper}>
                    <div className={styles.stepperTrack} />
                    {HOW_IT_WORKS_STEPS.map((step, index) => (
                        <div className={styles.stepperNode} key={step.title}>
                            <div className={styles.nodeDot}>{index + 1}</div>
                            <div className={styles.nodeTitle}>{step.title}</div>
                            <div className={styles.nodeSub}>{step.sub}</div>
                        </div>
                    ))}
                </div>
            </div>

            {isRegisterModalOpen && (
                <RegisterDeviceModal onClose={() => setIsRegisterModalOpen(false)} />
            )}

            {showUpgradeModal && (
                <UpgradeMembershipModal
                    onClose={handleUpgradeClose}
                    onGoBack={handleUpgradeClose}
                    paymentSuccess={isPaymentRedirect}
                    onContinue={handleUpgradeSuccess}
                />
            )}
        </div>
    );
}

export default PartnerDevice;
